import { spawnSync } from "node:child_process";
import { mkdtempSync, realpathSync, rmSync } from "node:fs";
import { tmpdir } from "node:os";
import { join } from "node:path";

/**
 * Git plumbing for `whop apps pull` and `whop apps deploy`.
 *
 * The platform stores source archives as snapshots, not history, so merges
 * synthesize their own base: a per-app ref (refs/whop/remote/<app_id>) tracks
 * successive remote snapshots as a client-side commit chain. Pull commits the
 * downloaded archive onto that ref and merges it into the user's branch —
 * giving git a real merge base from the second pull on. Deploy advances the
 * same ref to the just-uploaded source, so the machine that shipped last is
 * always up to date. If per-app git hosting ships later, this ref becomes a
 * real remote-tracking branch without changing the UX.
 */

const SPAWN_VIA_SHELL = process.platform === "win32";

type GitResult = {
	status: number | null;
	stdout: string;
	stderr: string;
	failed: boolean;
};

function runGit(
	dir: string,
	args: string[],
	env: Record<string, string> = {},
): GitResult {
	const result = spawnSync("git", args, {
		cwd: dir,
		encoding: "utf-8",
		shell: SPAWN_VIA_SHELL,
		env: { ...process.env, ...env },
		maxBuffer: 128 * 1024 * 1024,
	});
	return {
		status: result.status,
		stdout: (result.stdout ?? "").trim(),
		stderr: (result.stderr ?? "").trim(),
		failed: Boolean(result.error) || result.status !== 0,
	};
}

function mustGit(
	dir: string,
	args: string[],
	env: Record<string, string> = {},
): string {
	const result = runGit(dir, args, env);
	if (result.failed) {
		throw new Error(
			`\`git ${args.join(" ")}\` failed${result.status != null ? ` (exit ${result.status})` : ""}: ${result.stderr || result.stdout}`,
		);
	}
	return result.stdout;
}

export function gitInstalled(): boolean {
	const result = spawnSync("git", ["--version"], {
		stdio: "ignore",
		shell: SPAWN_VIA_SHELL,
	});
	return !result.error && result.status === 0;
}

/** The toplevel of the work tree containing dir, or null when outside any repo. */
export function repoToplevel(dir: string): string | null {
	const result = runGit(dir, ["rev-parse", "--show-toplevel"]);
	return result.failed ? null : result.stdout;
}

/**
 * Where dir stands relative to a git work tree. Git reports realpaths while
 * user-supplied paths may ride a symlink (macOS /tmp → /private/tmp), so the
 * comparison resolves both sides.
 */
export function repoStatus(
	dir: string,
): { kind: "none" } | { kind: "root" } | { kind: "nested"; toplevel: string } {
	const toplevel = repoToplevel(dir);
	if (toplevel === null) return { kind: "none" };
	return realpathSync(toplevel) === realpathSync(dir)
		? { kind: "root" }
		: { kind: "nested", toplevel };
}

export function gitInit(dir: string): void {
	mustGit(dir, ["init"]);
}

export function hasCommits(dir: string): boolean {
	return !runGit(dir, ["rev-parse", "-q", "--verify", "HEAD"]).failed;
}

export function isDirty(dir: string): boolean {
	return mustGit(dir, ["status", "--porcelain"]).length > 0;
}

export function remoteRef(appId: string): string {
	return `refs/whop/remote/${appId}`;
}

function refCommit(dir: string, ref: string): string | null {
	const result = runGit(dir, ["rev-parse", "-q", "--verify", ref]);
	return result.failed ? null : result.stdout;
}

/**
 * Commits without user.name/user.email configured fail — fall back to a CLI
 * identity rather than overriding one the user has set.
 */
function identityArgs(dir: string): string[] {
	const email = runGit(dir, ["config", "--get", "user.email"]);
	if (!email.failed && email.stdout) return [];
	return ["-c", "user.name=whop CLI", "-c", "user.email=cli@whop.com"];
}

export function commitAll(dir: string, message: string): void {
	mustGit(dir, ["add", "-A"]);
	mustGit(dir, [...identityArgs(dir), "commit", "-m", message, "--no-verify"]);
}

/**
 * Commits the contents of snapshotDir as the new tip of the app's remote-
 * snapshot ref, parented on the previous snapshot. With `deriveFromHead`
 * (deploy: the uploaded source was built from HEAD) the snapshot is also
 * parented on HEAD, which is what later gives this machine's pulls a real
 * merge base — but only when the snapshot deletes nothing relative to HEAD.
 * The source archive excludes paths like .env and dist/, so for a repo that
 * tracks such files the derivation claim would make them look remotely
 * deleted and a later pull would remove them from the working tree; those
 * repos keep the conservative unparented snapshot instead. The commit is
 * built with a throwaway index against snapshotDir as the work tree, so the
 * user's index and working tree are never touched. Returns the ref's commit
 * and whether the tree changed since the previous snapshot.
 */
export function commitSnapshot(
	repoDir: string,
	snapshotDir: string,
	appId: string,
	message: string,
	options: { deriveFromHead?: boolean } = {},
): { commit: string; changed: boolean } {
	const ref = remoteRef(appId);
	const gitDir = mustGit(repoDir, ["rev-parse", "--absolute-git-dir"]);
	const scratch = mkdtempSync(join(tmpdir(), "whop-git-"));
	try {
		const env = {
			GIT_DIR: gitDir,
			GIT_WORK_TREE: snapshotDir,
			GIT_INDEX_FILE: join(scratch, "index"),
		};
		mustGit(snapshotDir, ["add", "-A", "-f", "."], env);
		const tree = mustGit(snapshotDir, ["write-tree"], env);

		const prev = refCommit(repoDir, ref);
		const prevTree = prev
			? mustGit(repoDir, ["rev-parse", `${prev}^{tree}`])
			: null;
		if (prev && tree === prevTree) return { commit: prev, changed: false };

		const parents = [prev].filter(Boolean) as string[];
		if (options.deriveFromHead) {
			const head = headCommit(repoDir);
			if (
				head &&
				!parents.includes(head) &&
				deletesNothingFrom(repoDir, head, tree)
			) {
				parents.push(head);
			}
		}
		const commit = mustGit(repoDir, [
			...identityArgs(repoDir),
			"commit-tree",
			tree,
			...parents.flatMap((parent) => ["-p", parent]),
			"-m",
			message,
		]);
		mustGit(repoDir, ["update-ref", ref, commit]);
		return { commit, changed: true };
	} finally {
		rmSync(scratch, { recursive: true, force: true });
	}
}

/** True when going from `commit` to `tree` deletes no tracked file. */
function deletesNothingFrom(
	dir: string,
	commit: string,
	tree: string,
): boolean {
	return !runGit(dir, ["diff", "--quiet", "--diff-filter=D", commit, tree])
		.failed;
}

export function headCommit(dir: string): string | null {
	const result = runGit(dir, ["rev-parse", "-q", "--verify", "HEAD"]);
	return result.failed ? null : result.stdout;
}

/**
 * True when merging `commit` into HEAD would introduce nothing: the commit
 * shares a base with HEAD and adds no changes on top of it. Covers the
 * machine that deployed last — its snapshot ref is parented on the HEAD the
 * source was built from, so a pull of its own deploy has nothing to merge —
 * without requiring the snapshot to be an ancestor of HEAD.
 */
export function nothingToMerge(dir: string, commit: string): boolean {
	const base = runGit(dir, ["merge-base", "HEAD", commit]);
	if (base.failed) return false;
	return !runGit(dir, ["diff", "--quiet", base.stdout, commit]).failed;
}

/** True when `commit` is already reachable from HEAD (nothing to merge). */
export function isMerged(dir: string, commit: string): boolean {
	return !runGit(dir, ["merge-base", "--is-ancestor", commit, "HEAD"]).failed;
}

export type MergeResult =
	| { status: "merged" }
	| { status: "conflicts"; files: string[] };

/**
 * Merges a snapshot commit into HEAD. `--allow-unrelated-histories` covers
 * the first pull into a repo that predates any snapshot: with no common base
 * git conflicts on files that genuinely differ and cleanly takes the rest,
 * which is the honest outcome. On conflict the merge is left in place —
 * standard markers, resolve and commit — because that is the one conflict UX
 * every developer and coding agent already knows.
 */
export function mergeSnapshot(
	dir: string,
	commit: string,
	message: string,
): MergeResult {
	const result = runGit(dir, [
		...identityArgs(dir),
		"merge",
		"--allow-unrelated-histories",
		"-m",
		message,
		commit,
	]);
	if (!result.failed) return { status: "merged" };

	const conflicts = runGit(dir, ["diff", "--name-only", "--diff-filter=U"]);
	if (!conflicts.failed && conflicts.stdout) {
		return { status: "conflicts", files: conflicts.stdout.split("\n") };
	}
	throw new Error(`\`git merge\` failed: ${result.stderr || result.stdout}`);
}

/** Points the current (possibly unborn) branch at a commit — the empty-repo case. */
export function resetHard(dir: string, commit: string): void {
	mustGit(dir, ["reset", "--hard", commit]);
}

export type StashResult = "stashed" | "clean";

export function stashPush(dir: string, message: string): StashResult {
	const before = runGit(dir, ["stash", "list"]).stdout;
	mustGit(dir, ["stash", "push", "--include-untracked", "--message", message]);
	const after = runGit(dir, ["stash", "list"]).stdout;
	return after !== before ? "stashed" : "clean";
}

/** Pops the autostash; false when the pop conflicted and the stash was kept. */
export function stashPop(dir: string): boolean {
	return !runGit(dir, ["stash", "pop"]).failed;
}
